import { useEffect, useState } from "react";
import { useNavigate, Outlet, Link, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { LogOut, Home, Users as UsersIcon, LineChart, MessageCircle, LayoutTemplate, User2 } from "lucide-react";
import { LogoMark } from "@/components/dw/Logo";

export default function AppShell() {
  const nav = useNavigate();
  const loc = useLocation();
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/app/me")
      .then((r) => { setMe(r.data); setLoading(false); })
      .catch(() => nav("/app/login"));
  }, [nav]);

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    nav("/app/login");
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center">Loading…</div>;
  if (!me) return null;

  const role = me.user?.role;

  const tabs = role === "client" ? [
    { to: "/app", icon: Home, label: "Home" },
    { to: "/app/weight", icon: LineChart, label: "Weight" },
    { to: "/app/chat", icon: MessageCircle, label: "Chat" },
    { to: "/app/profile", icon: User2, label: "Me" },
  ] : role === "dietitian" ? [
    { to: "/app", icon: UsersIcon, label: "Clients" },
    { to: "/app/templates", icon: LayoutTemplate, label: "Templates" },
    { to: "/app/profile", icon: User2, label: "Me" },
  ] : [
    { to: "/app", icon: UsersIcon, label: "Clients" },
    { to: "/app/templates", icon: LayoutTemplate, label: "Templates" },
    { to: "/app/team", icon: UsersIcon, label: "Team" },
    { to: "/app/profile", icon: User2, label: "Me" },
  ];

  return (
    <div className="min-h-screen pb-24" style={{ background: "var(--bg-paper)" }}>
      <header className="sticky top-0 z-30 border-b" style={{ background: "var(--bg)", borderColor: "var(--border-subtle)" }}>
        <div className="max-w-md mx-auto px-4 h-14 flex items-center justify-between">
          <Link to="/app" className="flex items-center gap-2">
            <LogoMark size={32} />
            <span className="font-display text-lg" style={{ color: "var(--primary)" }}>DietWalaa</span>
          </Link>
          <button data-testid="app-logout" onClick={logout} className="text-xs font-bold flex items-center gap-1 opacity-70">
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </header>
      <main className="max-w-md mx-auto px-4 pt-4 pb-24">
        <Outlet context={{ me, refresh: () => api.get("/app/me").then((r) => setMe(r.data)) }} />
      </main>

      <nav className="fixed bottom-0 left-0 right-0 z-40 border-t" style={{ background: "var(--bg)", borderColor: "var(--border-subtle)", paddingBottom: "env(safe-area-inset-bottom)" }}>
        <div className="max-w-md mx-auto flex items-stretch justify-around h-16">
          {tabs.map((t) => {
            const Icon = t.icon;
            const active = loc.pathname === t.to || (t.to !== "/app" && loc.pathname.startsWith(t.to));
            return (
              <Link key={t.to} to={t.to} data-testid={`tab-${t.label.toLowerCase()}`} className="flex-1 flex flex-col items-center justify-center gap-0.5" style={{ color: active ? "var(--primary)" : "var(--text-secondary)" }}>
                <Icon size={20} strokeWidth={active ? 2.5 : 2} />
                <span className="text-[10px] font-bold uppercase tracking-widest">{t.label}</span>
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
